import { Eye, EyeOff } from 'lucide-react'
import { forwardRef, useId, useState } from 'react'
import { cn } from './cn'

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
	label?: React.ReactNode
	hint?: React.ReactNode
	error?: React.ReactNode
	leadingIcon?: React.ReactNode
	trailingIcon?: React.ReactNode
	/** For type="password": render eye toggle. */
	withPasswordToggle?: boolean
	/** Accessibility label for the password-visibility toggle. Pass a
	 * translated string when using `withPasswordToggle`. */
	showPasswordLabel?: string
	hidePasswordLabel?: string
	block?: boolean
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
	{
		label,
		hint,
		error,
		leadingIcon,
		trailingIcon,
		withPasswordToggle,
		showPasswordLabel = 'Show password',
		hidePasswordLabel = 'Hide password',
		block = true,
		className,
		id,
		type = 'text',
		required,
		...rest
	},
	ref,
) {
	const generatedId = useId()
	const inputId = id ?? generatedId
	const [visible, setVisible] = useState(false)
	const effectiveType = type === 'password' && withPasswordToggle && visible ? 'text' : type
	const hasLeading = !!leadingIcon
	const hasTrailing = !!trailingIcon || (type === 'password' && withPasswordToggle)

	return (
		<div className={cn('form-control', block && 'w-full')}>
			{label && (
				<label htmlFor={inputId} className="label pb-1.5">
					<span className="label-text font-medium text-sm">
						{label}
						{required && (
							<span className="text-error ml-0.5" aria-hidden="true">
								*
							</span>
						)}
					</span>
				</label>
			)}
			<div className="relative">
				{leadingIcon && (
					<span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 pointer-events-none">
						{leadingIcon}
					</span>
				)}
				<input
					ref={ref}
					id={inputId}
					type={effectiveType}
					required={required}
					className={cn(
						'input-refined w-full',
						hasLeading && 'pl-10',
						hasTrailing && 'pr-10',
						error && 'border-error focus:border-error',
						className,
					)}
					aria-invalid={error ? true : undefined}
					aria-describedby={error || hint ? `${inputId}-help` : undefined}
					{...rest}
				/>
				{type === 'password' && withPasswordToggle ? (
					/* F-005: hit area expanded to 42×42 via padding so the eye-icon button
					 * passes WCAG 2.5.8 (Target Size, AA in WCAG 2.2 / informational SC 2.5.5
					 * in WCAG 2.1). `right-0` + `p-3` keeps the icon visually centered at the
					 * same pixel position as before (parent-right - 21 px). */
					<button
						type="button"
						onClick={() => setVisible((v) => !v)}
						className="absolute right-0 top-1/2 -translate-y-1/2 p-3 text-base-content/50 hover:text-base-content"
						aria-label={visible ? hidePasswordLabel : showPasswordLabel}
					>
						{visible ? <EyeOff size={18} /> : <Eye size={18} />}
					</button>
				) : trailingIcon ? (
					<span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50">
						{trailingIcon}
					</span>
				) : null}
			</div>
			{(error || hint) && (
				<p
					id={`${inputId}-help`}
					role={error ? 'alert' : undefined}
					className={cn('text-xs mt-1.5', error ? 'text-error' : 'text-base-content/60')}
				>
					{error ?? hint}
				</p>
			)}
		</div>
	)
})
